import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import SafeIcon from '../common/SafeIcon';
import * as LuIcons from 'react-icons/lu';

const { 
  LuCar, LuAnchor, LuTruck, LuCaravan, 
  LuCircleCheck, LuAward, LuClock, LuZap, 
  LuShield, LuCircleAlert, LuArrowRight, LuRotateCcw 
} = LuIcons;

const ShowcaseSection = () => {
  const { t } = useTranslation();
  
  // Showcase State
  const [activeType, setActiveType] = useState('car');
  
  // Advisor State
  const [step, setStep] = useState(1);
  const [selections, setSelections] = useState({ type: null, usage: null, concern: null });

  const brands = ["Volvo", "Volkswagen", "Audi", "BMW", "Toyota", "Yamaha", "Volvo Penta", "Mercury", "Yanmar", "Adria"];
  
  const types = [
    { id: 'car', icon: LuCar, color: '#8377BE' },
    { id: 'marine', icon: LuAnchor, color: '#17066E' },
    { id: 'motorhome', icon: LuTruck, color: '#50487A' },
    { id: 'trailer', icon: LuCaravan, color: '#848489' }
  ];

  const usageOptions = [
    { id: 'daily', icon: LuClock },
    { id: 'seasonal', icon: LuZap },
    { id: 'stored', icon: LuShield }
  ];

  const concernOptions = [
    { id: 'maintenance', icon: LuCircleCheck },
    { id: 'safety', icon: LuShield },
    { id: 'prep', icon: LuZap },
    { id: 'issue', icon: LuCircleAlert }
  ];

  const handleAdvisorSelect = (key, value) => {
    setSelections(prev => ({ ...prev, [key]: value }));
    setStep(prev => prev + 1);
  };

  const getRecommendation = () => {
    const { type } = selections;
    const services = t(`services.${type}.items`, { returnObjects: true }) || [];
    return {
      title: t(`services.${type}.title`),
      items: services.slice(0, 3)
    };
  };

  return (
    <section id="showcase" className="section-container pt-[86px]">
      {/* --- BRAND TICKER --- */}
      <div className="brand-ticker-container mb-14"> {/* Reduced from mb-20 (30% reduction) */}
        <p className="brand-label text-center mb-4">{t('showcase.brandsTitle')}</p> {/* Reduced from mb-6 */}
        <div className="ticker-wrapper">
          <motion.div 
            className="ticker-content" 
            animate={{ x: [0, -1000] }} 
            transition={{ repeat: Infinity, duration: 30, ease: "linear" }}
          >
            {[...brands, ...brands].map((brand, i) => (
              <span key={i} className="brand-item">{brand}</span>
            ))}
          </motion.div>
        </div>
      </div>

      <div className="hub-grid">
        {/* --- LEFT COLUMN: SPECIALIZATION VIEWER --- */}
        <motion.div 
          className="hub-card glass-card"
          initial={{ opacity: 0, x: -30 }}
          whileInView={{ opacity: 1, x: 0 }}
          viewport={{ once: true }}
        >
          <div className="card-header-compact">
            <SafeIcon icon={LuAward} className="text-soft-purple" />
            <h3>{t('showcase.interactiveTitle')}</h3>
          </div>

          <div className="viewer-mini-layout">
            <div className="viewer-tabs">
              {types.map((type) => (
                <button 
                  key={type.id} 
                  onClick={() => setActiveType(type.id)}
                  className={`mini-tab ${activeType === type.id ? 'active' : ''}`}
                  style={{ '--accent': type.color }}
                >
                  <SafeIcon icon={type.icon} />
                </button>
              ))}
            </div>

            <div className="viewer-display-area">
              <AnimatePresence mode="wait">
                <motion.div 
                  key={activeType}
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -10 }}
                  className="mini-display-content"
                >
                  <div className="blueprint-orb">
                    <SafeIcon icon={types.find(t => t.id === activeType).icon} />
                  </div>
                  <div className="mini-info">
                    <h4>{t(`showcase.${activeType}.title`)}</h4>
                    <ul className="mini-focus-list">
                      {t(`showcase.${activeType}.focus`, { returnObjects: true }).map((point, i) => (
                        <li key={i}><SafeIcon icon={LuCircleCheck} /> {point}</li>
                      ))}
                    </ul>
                  </div>
                </motion.div>
              </AnimatePresence>
            </div>
          </div>
        </motion.div>

        {/* --- RIGHT COLUMN: SMART ADVISOR --- */}
        <motion.div 
          className="hub-card glass-card advisor-card"
          initial={{ opacity: 0, x: 30 }}
          whileInView={{ opacity: 1, x: 0 }}
          viewport={{ once: true }}
        >
          <div className="card-header-compact">
            <SafeIcon icon={LuZap} className="text-yellow-400" />
            <h3>{t('recommendations.title')}</h3>
          </div>

          <div className="wizard-mini-content">
            <AnimatePresence mode="wait">
              {step === 1 && (
                <motion.div key="s1" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}>
                  <p className="step-label">{t('recommendations.steps.type')}</p>
                  <div className="mini-options">
                    {types.map(item => (
                      <button key={item.id} className="mini-option-btn" onClick={() => handleAdvisorSelect('type', item.id)}>
                        <SafeIcon icon={item.icon} />
                        <span>{t(`services.${item.id}.title`)}</span>
                      </button>
                    ))}
                  </div>
                </motion.div>
              )}

              {step === 2 && (
                <motion.div key="s2" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}>
                  <p className="step-label">{t('recommendations.steps.usage')}</p>
                  <div className="mini-options grid-cols-1">
                    {usageOptions.map(item => (
                      <button key={item.id} className="mini-option-row" onClick={() => handleAdvisorSelect('usage', item.id)}>
                        <SafeIcon icon={item.icon} />
                        <span>{t(`recommendations.usageOptions.${item.id}`)}</span>
                      </button>
                    ))}
                  </div>
                </motion.div>
              )}

              {step === 3 && (
                <motion.div key="s3" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}>
                  <p className="step-label">{t('recommendations.steps.concern')}</p>
                  <div className="mini-options">
                    {concernOptions.map(item => (
                      <button key={item.id} className="mini-option-btn" onClick={() => handleAdvisorSelect('concern', item.id)}>
                        <SafeIcon icon={item.icon} />
                        <span>{t(`recommendations.concerns.${item.id}`)}</span>
                      </button>
                    ))}
                  </div>
                </motion.div>
              )}

              {step === 4 && (
                <motion.div key="res" initial={{ opacity: 0, scale: 0.9 }} animate={{ opacity: 1, scale: 1 }}>
                  <div className="mini-result">
                    <div className="res-icon"><SafeIcon icon={LuCircleCheck} /></div>
                    <h5>{getRecommendation().title}</h5>
                    <ul className="res-list">
                      {getRecommendation().items.map((item, i) => (
                        <li key={i}>{item}</li>
                      ))}
                    </ul>
                    <div className="res-actions">
                      <a href="#contact" className="gradient-button sm">{t('recommendations.results.cta')}</a>
                      <button onClick={() => setStep(1)} className="text-xs text-gray-500 hover:text-white mt-2 flex items-center gap-1">
                        <SafeIcon icon={LuRotateCcw} /> {t('recommendations.results.reset')}
                      </button>
                    </div>
                  </div>
                </motion.div>
              )}
            </AnimatePresence>
          </div>
          
          {step < 4 && (
            <div className="mini-progress-track">
              <motion.div 
                className="mini-progress-fill" 
                animate={{ width: `${(step / 3) * 100}%` }} 
              />
            </div>
          )}
        </motion.div>
      </div>

      <style jsx>{`
        #showcase {
          padding-bottom: 35px !important; /* Reduced from 50px (30% reduction) */
        }

        .hub-grid {
          display: grid;
          grid-template-columns: 1.2fr 1fr;
          gap: 2rem;
          max-width: 1540px; /* Maintained 10% increase from 1400px */
          margin: 0 auto;
          align-items: stretch;
        }

        .hub-card {
          padding: 2.5rem;
          display: flex;
          flex-direction: column;
          position: relative;
        }

        .card-header-compact {
          display: flex;
          align-items: center;
          gap: 0.75rem;
          margin-bottom: 1.4rem; /* Reduced from 2rem (30% reduction) */
          padding-bottom: 0.7rem; /* Reduced from 1rem */
          border-bottom: 1px solid rgba(221,219,230,0.1);
        }

        .card-header-compact h3 {
          font-size: 1.25rem;
          font-weight: 700;
          color: white;
          text-transform: uppercase;
          letter-spacing: 0.05em;
        }

        /* Ticker Styles */
        .brand-label { color: #8377BE; font-weight: 700; text-transform: uppercase; letter-spacing: 0.2em; font-size: 0.75rem; }
        .ticker-wrapper { overflow: hidden; white-space: nowrap; mask-image: linear-gradient(to right, transparent, black 15%, black 85%, transparent); }
        .ticker-content { display: inline-flex; gap: 5rem; }
        .brand-item { font-size: 2.2rem; font-weight: 900; color: rgba(221,219,230,0.05); transition: 0.3s; }
        .brand-item:hover { color: #8377BE; }

        /* Viewer Mini Styles */
        .viewer-mini-layout { display: flex; gap: 2rem; height: 100%; }
        .viewer-tabs { display: flex; flex-direction: column; gap: 0.75rem; }
        .mini-tab {
          width: 50px; height: 50px;
          border-radius: 12px;
          background: rgba(221,219,230,0.03);
          border: 1px solid rgba(221,219,230,0.1);
          color: #848489;
          display: flex; align-items: center; justify-content: center;
          cursor: pointer; transition: 0.3s;
        }
        .mini-tab.active { background: rgba(131,119,190,0.2); color: white; border-color: var(--accent); }

        .viewer-display-area { flex: 1; min-height: 250px; }
        .mini-display-content { display: flex; align-items: center; gap: 2rem; height: 100%; }
        .blueprint-orb {
          width: 120px; height: 120px;
          border-radius: 50%;
          border: 2px dashed rgba(131,119,190,0.3);
          display: flex; align-items: center; justify-content: center;
          font-size: 3rem; color: #8377BE;
          background: radial-gradient(circle, rgba(131,119,190,0.1) 0%, transparent 70%);
        }
        .mini-info h4 { font-size: 1.4rem; font-weight: 700; margin-bottom: 1rem; color: white; }
        .mini-focus-list { display: flex; flex-direction: column; gap: 0.5rem; }
        .mini-focus-list li { display: flex; align-items: center; gap: 0.5rem; font-size: 0.9rem; color: #848489; }

        /* Advisor Mini Styles */
        .step-label { font-size: 0.9rem; color: #848489; margin-bottom: 1rem; font-weight: 600; }
        .mini-options { display: grid; grid-template-columns: repeat(2, 1fr); gap: 0.75rem; }
        .mini-option-btn {
          padding: 1rem;
          background: rgba(221,219,230,0.03);
          border: 1px solid rgba(221,219,230,0.1);
          border-radius: 12px;
          display: flex; flex-direction: column; align-items: center; gap: 0.5rem;
          color: #848489; font-size: 0.8rem; font-weight: 600;
          transition: 0.3s;
        }
        .mini-option-btn:hover { background: rgba(131,119,190,0.1); color: white; border-color: #8377BE; }
        .mini-option-row {
          padding: 1rem;
          background: rgba(221,219,230,0.03);
          border: 1px solid rgba(221,219,230,0.1);
          border-radius: 12px;
          display: flex; align-items: center; gap: 1rem;
          color: #848489; font-size: 0.9rem; font-weight: 600;
          transition: 0.3s; width: 100%;
        }
        .mini-option-row:hover { background: rgba(131,119,190,0.1); color: white; border-color: #8377BE; }

        .mini-result { text-align: center; padding: 1rem 0; }
        .res-icon { font-size: 2.5rem; color: #10b981; margin-bottom: 1rem; }
        .res-list { margin: 1rem 0; text-align: left; background: rgba(0,0,0,0.2); padding: 1rem; border-radius: 12px; }
        .res-list li { font-size: 0.85rem; color: #DDDBE6; margin-bottom: 0.4rem; padding-left: 1rem; position: relative; }
        .res-list li::before { content: '→'; position: absolute; left: 0; color: #8377BE; }
        .gradient-button.sm { padding: 0.6rem 1.2rem; font-size: 0.85rem; }

        .mini-progress-track {
          position: absolute; bottom: 0; left: 0; width: 100%; height: 3px;
          background: rgba(221,219,230,0.05);
        }
        .mini-progress-fill { height: 100%; background: #8377BE; transition: 0.5s; }

        @media (max-width: 1024px) {
          .hub-grid { grid-template-columns: 1fr; }
          .viewer-mini-layout { flex-direction: column; }
          .viewer-tabs { flex-direction: row; justify-content: center; }
          .mini-display-content { flex-direction: column; text-align: center; }
        }

        @media (max-width: 768px) {
          #showcase {
            padding-bottom: 21px !important; /* Reduced from 30px (30% reduction) */
          }
          .brand-item { font-size: 1.6rem; }
          .hub-card { padding: 1.5rem; }
          .card-header-compact { margin-bottom: 1rem; }
          .mini-options { grid-template-columns: repeat(2, 1fr); }
          .blueprint-orb { width: 90px; height: 90px; font-size: 2rem; }
          .mini-info h4 { font-size: 1.15rem; }
        }

        @media (max-width: 480px) {
          #showcase {
            padding-bottom: 14px !important; /* Reduced from 20px (30% reduction) */
          }
          .mini-options { grid-template-columns: 1fr 1fr; gap: 0.5rem; }
          .mini-option-btn { padding: 0.75rem 0.5rem; font-size: 0.75rem; }
          .brand-item { font-size: 1.3rem; }
          .card-header-compact h3 { font-size: 1rem; }
          .res-actions { flex-direction: column; align-items: center; }
        }
      `}</style>
    </section>
  );
};

export default ShowcaseSection;